import { AlertTriangle, RefreshCcw, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/logo';

// Build: 2024-12-07-v1

interface ConfigurationErrorProps {
  missingVariables: string[];
}

export function ConfigurationError({ missingVariables }: ConfigurationErrorProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyEnvExample = () => {
    const envExample = missingVariables
      .map(v => `${v}=your-value-here`)
      .join('\n');
    
    navigator.clipboard.writeText(envExample).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard API may fail in some contexts (e.g., iframe without permissions)
    });
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const isProduction = import.meta.env.PROD;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="w-full max-w-lg rounded-2xl border bg-white p-8 text-center shadow-xl">
        <div className="mb-6">
          <Logo variant="dark" width={180} className="mx-auto" />
        </div>
        
        <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-warning/5">
          <AlertTriangle className="size-8 text-warning" />
        </div>
        
        <h1 className="mb-4 text-2xl font-bold text-gray-900">
          {isProduction ? 'Service Temporarily Unavailable' : 'Configuration Required'}
        </h1>
        
        {isProduction ? (
          <>
            <p className="mb-6 text-gray-600">
              We're experiencing technical difficulties. Our team has been notified and is working to resolve the issue.
            </p>
            <p className="mb-6 text-sm text-gray-500">
              Please try again in a few minutes.
            </p>
          </>
        ) : (
          <>
            <p className="mb-6 text-gray-600">
              The application cannot connect to the database. The following environment variables are missing:
            </p>
            
            <div className="mb-6 rounded-lg bg-gray-100 p-4 text-left">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Missing Variables:</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyEnvExample}
                  className="h-8 text-xs"
                >
                  {copied ? (
                    <>
                      <Check className="mr-1 size-3" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1 size-3" />
                      Copy template
                    </>
                  )}
                </Button>
              </div>
              <ul className="space-y-1">
                {missingVariables.map((varName) => (
                  <li key={varName} className="font-mono text-sm text-destructive">
                    • {varName}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="mb-6 rounded-lg border border-info/20 bg-info/5 p-4 text-left">
              <h3 className="mb-2 font-medium text-info-foreground">How to fix:</h3>
              <ol className="list-inside list-decimal space-y-1 text-sm text-info">
                <li>Copy <code className="rounded bg-info/10 px-1">.env.example</code> to <code className="rounded bg-info/10 px-1">.env</code></li>
                <li>Fill in your Supabase credentials from your dashboard</li>
                <li>Restart the development server</li>
              </ol>
            </div>
          </>
        )}
        
        <div className="flex justify-center gap-4">
          <Button onClick={handleRefresh} className="gap-2">
            <RefreshCcw className="size-4" />
            {isProduction ? 'Try Again' : 'Refresh'}
          </Button>
          
          {!isProduction && (
            <Button
              variant="outline"
              onClick={() => window.open('https://supabase.com/dashboard', '_blank')}
            >
              Open Supabase
            </Button>
          )}
        </div>
        
        {isProduction && (
          <p className="mt-6 text-xs text-gray-400">
            Error Code: CONFIG_MISSING
          </p>
        )}
      </div>
    </div>
  );
}
